/**
 * Phase 22 Pelora Billing (billing-edi HttpApi) agency contracts — r1-build-contract.md §3, §5.
 *
 * Every response goes through a normaliser: missing or wrongly typed fields get a safe default,
 * so a partial or older backend never crashes a screen. Money is integer cents.
 */

// ── Primitive readers ───────────────────────────────────────────────────────

type Raw = Record<string, unknown>;

function obj(value: unknown): Raw {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : {};
}
function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}
function optStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
function num(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function numOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function bool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
function strArr(value: unknown): string[] {
  return arr(value).filter((v): v is string => typeof v === 'string');
}
function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

// ── Constants (contract §2) ─────────────────────────────────────────────────

export const EDI_KINDS = ['837', '999', '277CA', '276', '277', '835', 'ELIG', 'RULE', 'USER'] as const;
export const WORK_KINDS = ['BLOCK', 'REJECTION', 'DENIAL', 'UNMATCHED', 'DEADLINE', 'INFO_REQUEST'] as const;
export const WORK_OWNERS = ['AUTOMATION', 'AGENCY', 'PELORA'] as const;
export const WORK_STATES = ['OPEN', 'IN_PROGRESS', 'WAITING_AGENCY', 'RESOLVED', 'WRITTEN_OFF'] as const;
export const SEND_MODES = ['review', 'auto'] as const;
export const BATCH_STATUSES = ['AWAITING_SEND', 'SENDING', 'SENT', 'HELD', 'PARTIAL'] as const;
export const ENROLL_STATUSES = ['NOT_STARTED', 'SUBMITTED', 'ACTIVE', 'FAILED', 'EXPIRING'] as const;
export const ENROLL_TX_TYPES = ['837', '835', '270'] as const;
export const WORK_ACTIONS = ['start', 'resolve', 'write_off', 'resubmit', 'correct', 'set_field', 'note', 'assign', 'snooze', 'reply'] as const;

export type EdiKind = (typeof EDI_KINDS)[number];
export type WorkKind = (typeof WORK_KINDS)[number];
export type WorkOwner = (typeof WORK_OWNERS)[number];
export type WorkState = (typeof WORK_STATES)[number];
export type SendMode = (typeof SEND_MODES)[number];
export type BatchStatus = (typeof BATCH_STATUSES)[number];
export type EnrollStatus = (typeof ENROLL_STATUSES)[number];
export type EnrollTxType = (typeof ENROLL_TX_TYPES)[number];
export type WorkAction = (typeof WORK_ACTIONS)[number];

// ── Dashboard (GET /edi/dashboard) ──────────────────────────────────────────

export interface EdiCountCents { count: number; cents: number }

export interface EdiTotals {
  billed: EdiCountCents;
  accepted: EdiCountCents;
  paid: EdiCountCents;
  denied: EdiCountCents;
  inFlight: EdiCountCents;
}

export interface EdiAgingRow {
  payerId: string;
  payerName: string;
  b0_30: number;
  b31_60: number;
  b61_90: number;
  b90: number;
}

export interface EdiDashboard {
  period: { month: string };
  totals: EdiTotals;
  lastMonth: EdiTotals;
  aging: EdiAgingRow[];
  touchlessRate: number | null;
  openExceptions: number;
  deadlinesSoon: number;
  sendMode: SendMode;
  simulated: boolean;
}

export function normalizeCountCents(value: unknown): EdiCountCents {
  const v = obj(value);
  return { count: num(v.count), cents: num(v.cents) };
}

export function normalizeTotals(value: unknown): EdiTotals {
  const v = obj(value);
  return {
    billed: normalizeCountCents(v.billed),
    accepted: normalizeCountCents(v.accepted),
    paid: normalizeCountCents(v.paid),
    denied: normalizeCountCents(v.denied),
    inFlight: normalizeCountCents(v.inFlight),
  };
}

export function normalizeAgingRow(value: unknown): EdiAgingRow {
  const v = obj(value);
  return {
    payerId: str(v.payerId),
    payerName: str(v.payerName, str(v.payerId, 'Unknown payer')),
    b0_30: num(v.b0_30),
    b31_60: num(v.b31_60),
    b61_90: num(v.b61_90),
    b90: num(v.b90),
  };
}

export function normalizeDashboard(value: unknown): EdiDashboard {
  const v = obj(value);
  const touchless = numOrNull(v.touchlessRate);
  return {
    period: { month: str(obj(v.period).month) },
    totals: normalizeTotals(v.totals),
    lastMonth: normalizeTotals(v.lastMonth),
    aging: arr(v.aging).map(normalizeAgingRow),
    // A fraction 0..1; a percentage (e.g. 82) is read as 0.82
    touchlessRate: touchless === null ? null : Math.min(1, Math.max(0, touchless > 1 ? touchless / 100 : touchless)),
    openExceptions: num(v.openExceptions),
    deadlinesSoon: num(v.deadlinesSoon),
    sendMode: oneOf(v.sendMode, SEND_MODES, 'review'),
    simulated: bool(v.simulated),
  };
}

// ── Batches (GET /edi/batches, /edi/batches/{id}) ────────────────────────────

export interface EdiBatch {
  batchId: string;
  createdAt: string;
  mode: SendMode;
  status: BatchStatus;
  claimIds: string[];
  heldClaimIds: string[];
  sentAt?: string;
  sentBy?: string;
  counts: { claims: number; accepted: number; rejected: number; pending: number };
  projectedChargeCents: number;
  chargeableCount: number;
}

export interface EdiBatchClaim {
  claimId: string;
  patientId: string;
  patientName: string;
  payerName: string;
  route: string;
  dosFrom: string;
  dosTo: string;
  chargeCents: number;
  lineCount: number;
  status: string;
  predictedIssues: string[];
  chargeable: boolean;
}

export interface EdiBatchDetail {
  batch: EdiBatch;
  claims: EdiBatchClaim[];
  /** Per-claim fee on Core without the add-on; null when claims are not charged one by one. */
  perClaimCents: number | null;
}

export function normalizeBatch(value: unknown): EdiBatch {
  const v = obj(value);
  const counts = obj(v.counts);
  const claimIds = strArr(v.claimIds);
  return {
    batchId: str(v.batchId),
    createdAt: str(v.createdAt),
    mode: oneOf(v.mode, SEND_MODES, 'review'),
    status: oneOf(v.status, BATCH_STATUSES, 'AWAITING_SEND'),
    claimIds,
    heldClaimIds: strArr(v.heldClaimIds),
    sentAt: optStr(v.sentAt),
    sentBy: optStr(v.sentBy),
    counts: {
      claims: num(counts.claims, claimIds.length),
      accepted: num(counts.accepted),
      rejected: num(counts.rejected),
      pending: num(counts.pending),
    },
    projectedChargeCents: num(v.projectedChargeCents),
    chargeableCount: num(v.chargeableCount),
  };
}

export function normalizeBatchClaim(value: unknown): EdiBatchClaim {
  const v = obj(value);
  return {
    claimId: str(v.claimId),
    patientId: str(v.patientId),
    patientName: str(v.patientName, 'Patient'),
    payerName: str(v.payerName),
    route: str(v.route),
    dosFrom: str(v.dosFrom),
    dosTo: str(v.dosTo, str(v.dosFrom)),
    chargeCents: num(v.chargeCents),
    lineCount: num(v.lineCount),
    status: str(v.status),
    predictedIssues: strArr(v.predictedIssues),
    chargeable: bool(v.chargeable),
  };
}

export function normalizeBatchDetail(value: unknown): EdiBatchDetail {
  const v = obj(value);
  return {
    batch: normalizeBatch(v.batch),
    claims: arr(v.claims).map(normalizeBatchClaim),
    perClaimCents: numOrNull(v.perClaimCents),
  };
}

/** POST /edi/batches/{id}/send: the batch plus the claims the send-time gate held back. */
export interface EdiBatchSendResult {
  batch: EdiBatch;
  refused: Array<{ claimId: string; issues: string[] }>;
}

export function normalizeBatchSendResult(value: unknown): EdiBatchSendResult {
  const v = obj(value);
  return {
    batch: normalizeBatch(v.batch),
    refused: arr(v.refused)
      .map((r) => {
        const e = obj(r);
        return { claimId: str(e.claimId), issues: strArr(e.issues) };
      })
      .filter((r) => r.claimId),
  };
}

export function normalizeBatchList(value: unknown): EdiBatch[] {
  return arr(obj(value).batches).map(normalizeBatch);
}

// ── Settings (GET/PUT /edi/settings) ─────────────────────────────────────────

export interface EdiSettings {
  sendMode: SendMode;
  sendModeSince: string;
  denialThreshold: number;
  enabledAt: string;
  version: number;
}

export interface UpdateEdiSettingsRequest {
  sendMode?: SendMode;
  denialThreshold?: number;
  confirm?: true;
}

export function normalizeSettings(value: unknown): EdiSettings {
  const v = obj(obj(value).settings);
  return {
    sendMode: oneOf(v.sendMode, SEND_MODES, 'review'),
    sendModeSince: str(v.sendModeSince),
    denialThreshold: num(v.denialThreshold, 0.5),
    enabledAt: str(v.enabledAt),
    version: num(v.version),
  };
}

// ── Claim timeline (GET /edi/claims/{id}/timeline) ──────────────────────────

export interface EdiSubmission {
  method: string;
  payerRoute: string;
  payerId: string;
  controlNumber: string;
  payerClaimControlNumber: string;
  batchId: string;
  attempt: number;
  firstSubmittedAt: string;
  lastSubmittedAt: string;
  chargeable: boolean;
}

export interface EdiAdjustment {
  groupCode: string;
  carc: string;
  rarc: string[];
  amountCents: number;
  lineNo?: number;
  at: string;
  takeback: boolean;
}

export interface EdiAdjudication {
  paidCents: number;
  allowedCents: number;
  patientRespCents: number;
  adjustments: EdiAdjustment[];
  remitIds: string[];
}

export interface EdiTimelineClaim {
  claimId: string;
  status: string;
  submission: EdiSubmission | null;
  adjudication: EdiAdjudication | null;
  filingDeadline: string | null;
  touchless: boolean | null;
  simulated: boolean;
}

export interface EdiClaimTx {
  at: string;
  kind: string;
  direction: 'OUT' | 'IN' | 'INTERNAL';
  transactionId?: string;
  outcome: string;
  fromStatus?: string;
  toStatus?: string;
  reasonCodes: string[];
  message?: string;
  source: string;
  actor: { type: string; id?: string };
  ruleId?: string;
  simulated: boolean;
}

export interface EdiClaimLine {
  lineNo: number;
  serviceDate: string;
  hcpcs: string;
  modifiers: string[];
  units: number;
  chargeCents: number;
  paidCents: number;
  allowedCents: number;
  carc: string[];
  rarc: string[];
  groupCode?: string;
  evvMatchCode?: string;
  status: 'PAID' | 'DENIED' | 'PARTIAL' | 'PENDING';
}

export interface EdiClaimTimeline {
  claim: EdiTimelineClaim;
  transactions: EdiClaimTx[];
  lines: EdiClaimLine[];
  touchlessReasons: string[];
}

export function normalizeSubmission(value: unknown): EdiSubmission | null {
  if (!value || typeof value !== 'object') return null;
  const v = obj(value);
  return {
    method: str(v.method),
    payerRoute: str(v.payerRoute),
    payerId: str(v.payerId),
    controlNumber: str(v.controlNumber),
    payerClaimControlNumber: str(v.payerClaimControlNumber),
    batchId: str(v.batchId),
    attempt: num(v.attempt, 1),
    firstSubmittedAt: str(v.firstSubmittedAt),
    lastSubmittedAt: str(v.lastSubmittedAt),
    chargeable: bool(v.chargeable),
  };
}

export function normalizeAdjustment(value: unknown): EdiAdjustment {
  const v = obj(value);
  const lineNo = numOrNull(v.lineNo);
  return {
    groupCode: str(v.groupCode),
    carc: str(v.carc),
    rarc: strArr(v.rarc),
    amountCents: num(v.amountCents),
    ...(lineNo === null ? {} : { lineNo }),
    at: str(v.at),
    takeback: bool(v.takeback),
  };
}

export function normalizeAdjudication(value: unknown): EdiAdjudication | null {
  if (!value || typeof value !== 'object') return null;
  const v = obj(value);
  return {
    paidCents: num(v.paidCents),
    allowedCents: num(v.allowedCents),
    patientRespCents: num(v.patientRespCents),
    adjustments: arr(v.adjustments).map(normalizeAdjustment),
    remitIds: strArr(v.remitIds),
  };
}

export function normalizeTimelineClaim(value: unknown): EdiTimelineClaim {
  const v = obj(value);
  return {
    claimId: str(v.claimId),
    status: str(v.status),
    submission: normalizeSubmission(v.submission),
    adjudication: normalizeAdjudication(v.adjudication),
    filingDeadline: optStr(v.filingDeadline) ?? null,
    touchless: typeof v.touchless === 'boolean' ? v.touchless : null,
    simulated: bool(v.simulated),
  };
}

export function normalizeClaimTx(value: unknown): EdiClaimTx {
  const v = obj(value);
  const actor = obj(v.actor);
  return {
    at: str(v.at),
    kind: str(v.kind),
    direction: oneOf(v.direction, ['OUT', 'IN', 'INTERNAL'] as const, 'INTERNAL'),
    transactionId: optStr(v.transactionId),
    outcome: str(v.outcome),
    fromStatus: optStr(v.fromStatus),
    toStatus: optStr(v.toStatus),
    reasonCodes: strArr(v.reasonCodes),
    message: optStr(v.message),
    source: str(v.source, 'SYSTEM'),
    actor: { type: str(actor.type, 'system'), id: optStr(actor.id) },
    ruleId: optStr(v.ruleId),
    simulated: bool(v.simulated) || str(v.source) === 'SIMULATED',
  };
}

export function normalizeClaimLine(value: unknown): EdiClaimLine {
  const v = obj(value);
  return {
    lineNo: num(v.lineNo),
    serviceDate: str(v.serviceDate),
    hcpcs: str(v.hcpcs),
    modifiers: strArr(v.modifiers),
    units: num(v.units),
    chargeCents: num(v.chargeCents),
    paidCents: num(v.paidCents),
    allowedCents: num(v.allowedCents),
    carc: strArr(v.carc),
    rarc: strArr(v.rarc),
    groupCode: optStr(v.groupCode),
    evvMatchCode: optStr(v.evvMatchCode),
    status: oneOf(v.status, ['PAID', 'DENIED', 'PARTIAL', 'PENDING'] as const, 'PENDING'),
  };
}

export function normalizeTimeline(value: unknown): EdiClaimTimeline {
  const v = obj(value);
  const transactions = arr(v.transactions).map(normalizeClaimTx);
  transactions.sort((a, b) => a.at.localeCompare(b.at));
  return {
    claim: normalizeTimelineClaim(v.claim),
    transactions,
    lines: arr(v.lines).map(normalizeClaimLine).sort((a, b) => a.lineNo - b.lineNo),
    touchlessReasons: strArr(v.touchlessReasons),
  };
}

// ── Worklist (GET /edi/worklist, /edi/worklist/{id}, POST …/actions) ────────

export interface EdiWorkReason {
  code: string;
  text: string;
  carc?: string;
  rarc?: string[];
  evv?: string;
}

export interface EdiWorkActionEntry {
  at: string;
  actor: string;
  action: string;
  result: string;
  note?: string;
}

export interface EdiWorkItem {
  workItemId: string;
  claimId?: string;
  patientId?: string;
  payerId?: string;
  patientName?: string;
  payerName?: string;
  kind: WorkKind;
  reason: EdiWorkReason;
  amountCents: number | null;
  deadline: string | null;
  owner: WorkOwner;
  assigneeUserId?: string;
  state: WorkState;
  suggestedAction: { type: string; params: Record<string, unknown> } | null;
  actions: EdiWorkActionEntry[];
  ruleId?: string;
  snoozedUntil?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  simulated: boolean;
}

export interface EdiWorklistOption {
  id: string;
  name: string;
}

export interface EdiWorklistPage {
  items: EdiWorkItem[];
  cursor: string | null;
  /** Filter dropdown options from the server (all open items, not just this page). */
  filters: { payers: EdiWorklistOption[]; patients: EdiWorklistOption[] };
}

export interface EdiWorkItemDetail {
  item: EdiWorkItem;
  claim: EdiTimelineClaim | null;
}

export interface EdiWorklistQuery {
  kind?: WorkKind | '';
  state?: WorkState | '';
  payerId?: string;
  patientId?: string;
  cursor?: string;
}

export interface EdiWorkActionRequest {
  action: WorkAction;
  params?: Record<string, unknown>;
  note?: string;
}

function actorText(value: unknown): string {
  if (typeof value === 'string') return value;
  const v = obj(value);
  return str(v.id, str(v.type, 'system'));
}

export function normalizeWorkItem(value: unknown): EdiWorkItem {
  const v = obj(value);
  const reason = obj(v.reason);
  const suggested = v.suggestedAction && typeof v.suggestedAction === 'object' ? obj(v.suggestedAction) : null;
  return {
    workItemId: str(v.workItemId),
    claimId: optStr(v.claimId),
    patientId: optStr(v.patientId),
    payerId: optStr(v.payerId),
    patientName: optStr(v.patientName),
    payerName: optStr(v.payerName),
    kind: oneOf(v.kind, WORK_KINDS, 'BLOCK'),
    reason: {
      code: str(reason.code),
      text: str(reason.text),
      carc: optStr(reason.carc),
      rarc: Array.isArray(reason.rarc) ? strArr(reason.rarc) : optStr(reason.rarc) ? [str(reason.rarc)] : undefined,
      evv: optStr(reason.evv),
    },
    amountCents: numOrNull(v.amountCents),
    deadline: optStr(v.deadline) ?? null,
    owner: oneOf(v.owner, WORK_OWNERS, 'AGENCY'),
    assigneeUserId: optStr(v.assigneeUserId),
    state: oneOf(v.state, WORK_STATES, 'OPEN'),
    suggestedAction: suggested ? { type: str(suggested.type), params: obj(suggested.params) } : null,
    actions: arr(v.actions).map((a) => {
      const e = obj(a);
      return { at: str(e.at), actor: actorText(e.actor), action: str(e.action), result: str(e.result), note: optStr(e.note) };
    }),
    ruleId: optStr(v.ruleId),
    snoozedUntil: optStr(v.snoozedUntil),
    createdAt: str(v.createdAt),
    updatedAt: str(v.updatedAt),
    version: num(v.version),
    simulated: bool(v.simulated),
  };
}

function worklistOptions(value: unknown, idKey: 'payerId' | 'patientId'): EdiWorklistOption[] {
  return arr(value)
    .map((o) => {
      const e = obj(o);
      const id = str(e[idKey]);
      return { id, name: str(e.name) || id };
    })
    .filter((o) => o.id);
}

export function normalizeWorklist(value: unknown): EdiWorklistPage {
  const v = obj(value);
  const filters = obj(v.filters);
  return {
    items: arr(v.items).map(normalizeWorkItem),
    cursor: optStr(v.cursor) ?? null,
    filters: { payers: worklistOptions(filters.payers, 'payerId'), patients: worklistOptions(filters.patients, 'patientId') },
  };
}

export function normalizeWorkItemDetail(value: unknown): EdiWorkItemDetail {
  const v = obj(value);
  return {
    item: normalizeWorkItem(v.item),
    claim: v.claim && typeof v.claim === 'object' ? normalizeTimelineClaim(v.claim) : null,
  };
}

export function normalizeWorkItemResponse(value: unknown): EdiWorkItem {
  return normalizeWorkItem(obj(value).item);
}

// ── Eligibility (GET /edi/eligibility/{patientId}, POST …/check) ─────────────

/**
 * Contract §5 names `eligibility` but not its fields; this is the shape the card reads
 * (reported to BE-W): checkedAt, status, source, message, simulated.
 */
export interface EdiEligibility {
  checkedAt: string;
  status: string;
  source: string;
  message?: string;
  simulated: boolean;
}

export interface EdiCoverageEligibility {
  coverageId: string;
  payerName: string;
  memberId: string;
  eligibility: EdiEligibility | null;
}

export function normalizeEligibility(value: unknown): EdiEligibility | null {
  if (!value || typeof value !== 'object') return null;
  const v = obj(value);
  const source = str(v.source, 'SYSTEM');
  return {
    checkedAt: str(v.checkedAt, str(v.at)),
    status: str(v.status, str(v.result, 'UNKNOWN')),
    source,
    message: optStr(v.message),
    simulated: bool(v.simulated) || source === 'SIMULATED',
  };
}

export function normalizeCoverageEligibilityList(value: unknown): EdiCoverageEligibility[] {
  return arr(obj(value).coverages).map((c) => {
    const v = obj(c);
    return {
      coverageId: str(v.coverageId),
      payerName: str(v.payerName, 'Payer'),
      memberId: str(v.memberId),
      eligibility: normalizeEligibility(v.eligibility),
    };
  });
}

// ── Enrollment (GET /edi/enrollments) ───────────────────────────────────────

export interface EdiEnrollment {
  payerId: string;
  txType: EnrollTxType;
  status: EnrollStatus;
  submittedAt?: string;
  activeAt?: string;
  expiresAt?: string;
  simulated: boolean;
}

export interface EdiEnrollments {
  enrollments: EdiEnrollment[];
  payers: Array<{ payerId: string; name: string }>;
}

export function normalizeEnrollments(value: unknown): EdiEnrollments {
  const v = obj(value);
  return {
    enrollments: arr(v.enrollments).map((e) => {
      const x = obj(e);
      return {
        payerId: str(x.payerId),
        txType: oneOf(x.txType, ENROLL_TX_TYPES, '837'),
        status: oneOf(x.status, ENROLL_STATUSES, 'NOT_STARTED'),
        submittedAt: optStr(x.submittedAt),
        activeAt: optStr(x.activeAt),
        expiresAt: optStr(x.expiresAt),
        simulated: bool(x.simulated),
      };
    }),
    payers: arr(v.payers).map((p) => {
      const x = obj(p);
      return { payerId: str(x.payerId), name: str(x.name, str(x.payerId)) };
    }),
  };
}

// ── Deadlines (GET /edi/deadlines) ──────────────────────────────────────────

export interface EdiDeadlineClaim {
  claimId: string;
  patientName: string;
  payerName: string;
  filingDeadline: string;
  daysLeft: number;
  status: string;
  amountCents: number;
}

export function normalizeDeadlines(value: unknown): EdiDeadlineClaim[] {
  return arr(obj(value).claims)
    .map((c) => {
      const v = obj(c);
      return {
        claimId: str(v.claimId),
        patientName: str(v.patientName, 'Patient'),
        payerName: str(v.payerName),
        filingDeadline: str(v.filingDeadline),
        daysLeft: num(v.daysLeft),
        status: str(v.status),
        amountCents: num(v.amountCents),
      };
    })
    .sort((a, b) => a.daysLeft - b.daysLeft);
}

// ── Add-on (GET /edi/addon) ─────────────────────────────────────────────────

export interface EdiAddon {
  eligible: boolean;
  reason?: string;
  enabled: boolean;
  perPatientCents: number | null;
  projectedMonthlyCents: number | null;
  perClaimCents: number | null;
  claimsThisPeriod: number;
  projectedClaimsCents: number | null;
}

export function normalizeAddon(value: unknown): EdiAddon {
  const v = obj(value);
  return {
    eligible: bool(v.eligible),
    reason: optStr(v.reason),
    enabled: bool(v.enabled),
    perPatientCents: numOrNull(v.perPatientCents),
    projectedMonthlyCents: numOrNull(v.projectedMonthlyCents),
    perClaimCents: numOrNull(v.perClaimCents),
    claimsThisPeriod: num(v.claimsThisPeriod),
    projectedClaimsCents: numOrNull(v.projectedClaimsCents),
  };
}

export function normalizeClaimResponse(value: unknown): EdiTimelineClaim {
  return normalizeTimelineClaim(obj(value).claim);
}
